import { useSuspenseQuery } from '@apollo/client';

import {
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  NewButton,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { ChatComponent } from './ChatComponent';
import { getChatConversationStartersQuery } from './chat.operations';

export const DEFAULT_STARTING_MESSAGES = [
  {
    id: 'trends',
    title: 'Positive + Negative Trends',
    description:
      'Analyze patient data to identify significant positive and negative trends, with recommended next steps.',
    content:
      "What are the positive and negative trends in the patient's data? Help me understand their significance and cite the data source and year when available. Recommend next steps. Be concise.",
  },
  {
    id: 'risk-factors',
    title: 'Risk Factors',
    description:
      "Present a list of top risk factors based on the patient's data, starting with the most urgent one.",
    content:
      'Based on the patient\'s data, what risk factors are present? Briefly explain your reasoning. Cite data and medical knowledge sources when available. Consider risk factors associated with lab data, medications, patient demographics, and others. Present up to 4 risk factors. Present a significant or clinically urgent risk factor first, if present. The last risk factor presented should be a "low" risk factor, that the patient may ease their worries about.',
  },
  {
    id: 'missing-pieces',
    title: 'Missing Pieces',
    description:
      "Finds gaps or missing information in the patient's record, and recommend what to ask the patient.",
    content:
      "Point out a few gaps or uncertainties in the patient's medical record, and briefly explain how this impacts prior conclusions and clinical decision making. Suggest the questions I can ask the patient to fill in these gaps.",
  },
];

const MAX_CONVERSATION_STARTERS = 3;

export const ChatWithPatientData = ({
  tenantId,
  patientId,
  baseUrl,
}: {
  tenantId: string;
  patientId: string;
  baseUrl: string;
}) => {
  const userId = useUserIdFromSession();
  const { data } = useSuspenseQuery(getChatConversationStartersQuery, {
    variables: { tenantId, userId },
  });

  const conversationStarters =
    data.tenantUserChatSettingsByPk?.conversationStarters ??
    DEFAULT_STARTING_MESSAGES;

  return (
    <DetailsSidebarProvider overlayOnly>
      <DetailsSidebarTrigger>
        <NewButton icon={{ name: 'MessageSquare' }} type="outline" />
      </DetailsSidebarTrigger>
      <DetailsSidebar size="lg" title="Patient Insights" allowFullscreen>
        <ChatComponent
          tenantId={tenantId}
          baseUrl={baseUrl}
          config={{
            patientId,
            startingMessages: conversationStarters.slice(
              0,
              MAX_CONVERSATION_STARTERS,
            ),
          }}
        />
      </DetailsSidebar>
    </DetailsSidebarProvider>
  );
};
